"use client";

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function SponsorshipPackages() {
  // OLD (not used for list now)
  const RAW_API_URL = "https://eventscons.com/eventbackendnew/api/package/table/admin";
  const API_URL = useMemo(() => sanitizeUrl(RAW_API_URL), []);

  // REQUIRED APIs
  const CONFERENCES_URL = "https://eventscons.com/eventbackendnew/api/conferences/all";
  const PACKAGES_BY_CONF_URL = (conferenceId) =>
    `https://eventscons.com/eventbackendnew/api/sponsorshippackages/${conferenceId}`;

  // existing endpoints (as in your code)
  const DELETE_URL = (id) =>
    `https://eventscons.com/eventbackendnew/api/sponsorshippackages/table/delete/${id}`;

  const EDIT_URL = (id) =>
    `https://eventscons.com/eventbackendnew/api/sponsorshippackages/table/edit/${id}`;

  // table rows
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // conferences dropdown
  const [conferences, setConferences] = useState([]);
  const [confLoading, setConfLoading] = useState(true);
  const [selectedConferenceId, setSelectedConferenceId] = useState("");

  // errors + action loaders
  const [err, setErr] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // delete confirm modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  // view modal
  const [viewModal, setViewModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [viewLoadingId, setViewLoadingId] = useState(null);

  // edit modal
  const [editModal, setEditModal] = useState(false);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // ✅ UPDATED: edit form with all fields
  const [editForm, setEditForm] = useState({
    conference_id: "",
    item: "",
    price: "",
    max_sponsors: "",
    video_show_between_sessions: "no",
    booth_size: "",
    booklet_ad: "",
    website_ad: "",
    bags_inserts: "",
    backdrop_logo: "",
    non_residential_reg: "",
    residential_reg: "",
  });

  // edit extras (dynamic)
  const [editExtras, setEditExtras] = useState([]);

  const USE_AUTH = false;

  const getAuthToken = () =>
    typeof window === "undefined"
      ? null
      : localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token");

  const apiHeaders = (isWrite = false) => {
    const token = USE_AUTH ? getAuthToken() : null;
    return {
      Accept: "application/json",
      ...(isWrite ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const normalizeArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.conferences)) return data.conferences;
    if (Array.isArray(data?.sponsorships)) return data.sponsorships;
    if (Array.isArray(data?.packages)) return data.packages;
    return [];
  };

  // ---------- helpers for extras ----------
  const extrasFromRecord = (record) => {
    const raw = record?.extras;

    if (Array.isArray(raw)) {
      return raw.map((x) => ({
        rowId: Date.now() + Math.random(),
        id: x?.id ?? null,
        label: x?.label ?? "",
        value: x?.value ?? "",
        _delete: false,
      }));
    }

    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((x) => ({
            rowId: Date.now() + Math.random(),
            id: x?.id ?? null,
            label: x?.label ?? "",
            value: x?.value ?? "",
            _delete: false,
          }));
        }
      } catch {
        // ignore
      }
    }

    return [];
  };

  const loadConferences = async () => {
    try {
      setConfLoading(true);
      setErr("");

      const res = await axios.get(CONFERENCES_URL, { headers: apiHeaders(false) });
      const list = normalizeArray(res.data);

      setConferences(list);

      if (!selectedConferenceId && list.length > 0) {
        const firstId = String(list[0]?.id ?? "");
        setSelectedConferenceId(firstId);
        setEditForm((p) => ({ ...p, conference_id: firstId }));
      }
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Conferences load failed (${status || "unknown"})`
      );
      setConferences([]);
    } finally {
      setConfLoading(false);
    }
  };

  const loadPackagesByConference = async (conferenceId) => {
    if (!conferenceId) {
      setRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErr("");

      const url = PACKAGES_BY_CONF_URL(conferenceId);
      const res = await axios.get(url, { headers: apiHeaders(false) });

      const list = normalizeArray(res.data);
      setRows(list);
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message ||
          e?.message ||
          `Packages load failed (${status || "unknown"})`
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageFromConference = async (conferenceId, packageId) => {
    const res = await axios.get(PACKAGES_BY_CONF_URL(conferenceId), {
      headers: apiHeaders(false),
    });
    const list = normalizeArray(res.data);
    return list.find((x) => String(x.id) === String(packageId)) || null;
  };

  // ✅ UPDATED: fetchPackageDetails with all fields
  const fetchPackageDetails = async (id, open = "view") => {
    try {
      setErr("");
      if (open === "view") setViewLoadingId(id);
      if (open === "edit") setEditLoadingId(id);

      const confId = selectedConferenceId;
      if (!confId) throw new Error("Conference not selected");

      const record = await fetchPackageFromConference(confId, id);
      if (!record) throw new Error("Package not found in conference API response");

      setSelectedPackage(record);

      if (open === "view") {
        setViewModal(true);
      } else {
        setEditForm({
          conference_id: String(record.conference_id ?? confId ?? ""),
          item: record.item ?? "",
          price: String(record.price ?? ""),
          max_sponsors: String(record.max_sponsors ?? ""),
          video_show_between_sessions:
            String(record.video_show_between_sessions ?? "no").toLowerCase() === "yes"
              ? "yes"
              : "no",
          booth_size: String(record.booth_size ?? ""),
          booklet_ad: String(record.booklet_ad ?? ""),
          website_ad: String(record.website_ad ?? ""),
          bags_inserts: String(record.bags_inserts ?? ""),
          backdrop_logo: String(record.backdrop_logo ?? ""),
          non_residential_reg: String(record.non_residential_reg ?? ""),
          residential_reg: String(record.residential_reg ?? ""),
        });

        setEditExtras(extrasFromRecord(record));
        setEditModal(true);
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to load package details");
    } finally {
      setViewLoadingId(null);
      setEditLoadingId(null);
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmId(id);
    setShowConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;

    try {
      setDeletingId(confirmId);
      setErr("");

      await axios.delete(DELETE_URL(confirmId), { headers: apiHeaders(false) });

      setRows((prev) => prev.filter((pkg) => pkg.id !== confirmId));
      if (selectedPackage?.id === confirmId) setSelectedPackage(null);
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message || e?.message || `Delete failed (${status || "unknown"})`
      );
    } finally {
      setDeletingId(null);
      setShowConfirm(false);
      setConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setConfirmId(null);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedPackage(null);
  };

  const closeEditModal = () => {
    setEditModal(false);
  };

  const onEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((p) => ({ ...p, [name]: value }));
  };

  // extras handlers (edit)
  const addEditExtra = () => {
    setEditExtras((prev) => [
      ...prev,
      { rowId: Date.now() + Math.random(), id: null, label: "", value: "", _delete: false },
    ]);
  };

  const updateEditExtra = (rowId, field, value) => {
    setEditExtras((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, [field]: value } : row))
    );
  };

  const deleteEditExtra = (rowId) => {
    setEditExtras((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, _delete: true } : row))
    );
  };

  // ✅ UPDATED: handleUpdate with all fields from formData
  const handleUpdate = async () => {
    if (!selectedPackage?.id) return;

    try {
      setSaving(true);
      setErr("");

      const id = selectedPackage.id;

      const cleanedExtras = editExtras
        .filter((x) => !x._delete)
        .map((x) => ({
          id: x.id || undefined,
          label: (x.label || "").trim(),
          value: String(x.value ?? "").trim(),
        }))
        .filter((x) => x.label !== "" || x.value !== "");

      // ✅ All fields now from editForm (user input)
      const payload = {
        conference_id: Number(editForm.conference_id || 0),
        item: (editForm.item || "").trim(),
        price: (editForm.price || "").trim(),
        max_sponsors: Number(editForm.max_sponsors || 0),
        video_show_between_sessions: editForm.video_show_between_sessions || "no",
        booth_size: (editForm.booth_size || "").trim(),
        booklet_ad: (editForm.booklet_ad || "").trim(),
        website_ad: (editForm.website_ad || "").trim(),
        bags_inserts: (editForm.bags_inserts || "").trim(),
        backdrop_logo: (editForm.backdrop_logo || "").trim(),
        non_residential_reg: Number(editForm.non_residential_reg || 0),
        residential_reg: Number(editForm.residential_reg || 0),
        extras: cleanedExtras,
      };

      const res = await axios.put(EDIT_URL(id), payload, { headers: apiHeaders(true) });
      const updated = res.data?.data ?? payload;

      await loadPackagesByConference(selectedConferenceId);
      setSelectedPackage((prev) => (prev ? { ...prev, ...updated } : prev));
      setEditModal(false);
    } catch (e) {
      const status = e?.response?.status;
      setErr(
        e?.response?.data?.message || e?.message || `Update failed (${status || "unknown"})`
      );
    } finally {
      setSaving(false);
    }
  };

  // initial load
  useEffect(() => {
    loadConferences();
  }, []);

  useEffect(() => {
    if (selectedConferenceId) loadPackagesByConference(selectedConferenceId);
  }, [selectedConferenceId]);

  if (loading) {
    return <div style={{ padding: 16, fontWeight: 400 }}>Loading sponsorship packages...</div>;
  }

  return (
    <>
      <div style={{ padding: 16, fontWeight: 400 }}>
        <h2 style={{ fontWeight: 400, margin: 0 }}>
          Sponsorship Packages ({rows.length})
        </h2>

        {/* TOP: Conference dropdown */}
        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 13, color: "#475569" }}>Conference</div>

          <select
            value={selectedConferenceId}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedConferenceId(v);
              setEditForm((p) => ({ ...p, conference_id: v }));
            }}
            disabled={confLoading || conferences.length === 0}
            style={selectStyle({ minWidth: 260 })}
          >
            {confLoading && <option value="">Loading conferences...</option>}
            {!confLoading && conferences.length === 0 && (
              <option value="">No conferences found</option>
            )}
            {!confLoading &&
              conferences.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name || c.title || c.conference_name || `Conference #${c.id}`}
                </option>
              ))}
          </select>

          <button
            onClick={() => loadPackagesByConference(selectedConferenceId)}
            disabled={!selectedConferenceId || loading}
            style={btnPrimary}
          >
            Load Packages
          </button>

          <button onClick={loadConferences} disabled={confLoading} style={btnGray}>
            {confLoading ? "Refreshing..." : "Refresh Conferences"}
          </button>
        </div>

        {err && <div style={errBox}>{err}</div>}

        {/* ✅ UPDATED: Table with all fields */}
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Item</Th>
                <Th>Price</Th>
                <Th>Max Sponsors</Th>
                <Th>Booth Size</Th>
                <Th>Booklet Ad</Th>
                <Th>Website Ad</Th>
                <Th>Actions</Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((pkg) => (
                <tr key={pkg.id}>
                  <Td>{pkg.id}</Td>
                  <Td>{pkg.item ?? "-"}</Td>
                  <Td>${pkg.price ?? "-"}</Td>
                  <Td>{pkg.max_sponsors ?? "-"}</Td>
                  <Td>{pkg.booth_size ?? "-"}</Td>
                  <Td>{pkg.booklet_ad ?? "-"}</Td>
                  <Td>{pkg.website_ad ?? "-"}</Td>

                  <Td style={{ whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => fetchPackageDetails(pkg.id, "view")}
                      disabled={viewLoadingId === pkg.id}
                      style={btnView(viewLoadingId === pkg.id)}
                    >
                      {viewLoadingId === pkg.id ? "Loading..." : "View"}
                    </button>

                    <button
                      onClick={() => fetchPackageDetails(pkg.id, "edit")}
                      disabled={editLoadingId === pkg.id}
                      style={btnEdit(editLoadingId === pkg.id)}
                    >
                      {editLoadingId === pkg.id ? "Loading..." : "Edit"}
                    </button>

                    <button
                      onClick={() => handleDeleteClick(pkg.id)}
                      disabled={deletingId === pkg.id}
                      style={btnDelete(deletingId === pkg.id)}
                    >
                      {deletingId === pkg.id ? "Deleting..." : "Delete"}
                    </button>
                  </Td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <Td colSpan={8} style={{ textAlign: "center", color: "#64748b" }}>
                    No sponsorship packages found
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button onClick={() => loadPackagesByConference(selectedConferenceId)} style={btnPrimary}>
          Refresh
        </button>
      </div>

      {/* Delete confirmation */}
      {showConfirm && (
        <Overlay onClose={handleCancelDelete}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px 0", color: "#1f2937", fontWeight: 400 }}>
              Delete Sponsorship Package?
            </h3>
            <p style={{ margin: "0 0 24px 0", color: "#6b7280", fontSize: 14, fontWeight: 400 }}>
              Are you sure you want to delete this sponsorship package? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button onClick={handleCancelDelete} style={btnGray}>
                Cancel
              </button>
              <button onClick={handleConfirmDelete} disabled={deletingId !== null} style={btnRed}>
                {deletingId === confirmId ? "Deleting..." : "Delete"}
              </button>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {/* View modal */}
      {viewModal && selectedPackage && (
        <Overlay onClose={closeViewModal}>
          <ModalBoxWide onClick={(e) => e.stopPropagation()}>
            <ModalHeader
              title={`Sponsorship Package #${selectedPackage.id}`}
              onClose={closeViewModal}
            />

            <div style={grid2}>
              <DetailItem label="Conference ID" value={selectedPackage.conference_id ?? "-"} />
              <DetailItem label="Item" value={selectedPackage.item ?? "-"} />
              <DetailItem label="Price" value={selectedPackage.price ?? "-"} />
              <DetailItem label="Max Sponsors" value={selectedPackage.max_sponsors ?? "-"} />
              <DetailItem
                label="Video Between Sessions"
                value={selectedPackage.video_show_between_sessions ?? "-"}
              />
              <DetailItem label="Booth Size" value={selectedPackage.booth_size ?? "-"} />
              <DetailItem label="Booklet Ad" value={selectedPackage.booklet_ad ?? "-"} />
              <DetailItem label="Website Ad" value={selectedPackage.website_ad ?? "-"} />
              <DetailItem label="Bags Inserts" value={selectedPackage.bags_inserts ?? "-"} />
              <DetailItem label="Backdrop Logo" value={selectedPackage.backdrop_logo ?? "-"} />
              <DetailItem
                label="Non-Residential Reg"
                value={selectedPackage.non_residential_reg ?? "-"}
              />
              <DetailItem label="Residential Reg" value={selectedPackage.residential_reg ?? "-"} />
              <DetailItem label="Created At" value={fmtDate(selectedPackage.created_at)} />
              <DetailItem label="Updated At" value={fmtDate(selectedPackage.updated_at)} />
            </div>

            {/* Extras preview */}
            <div style={{ marginTop: 18 }}>
              <h4 style={{ margin: "0 0 10px 0", fontWeight: 400, color: "#111827" }}>
                Extras
              </h4>
              <div style={{ display: "grid", gap: 10 }}>
                {(() => {
                  const list = extrasFromRecord(selectedPackage).filter((x) => !x._delete);
                  if (list.length === 0) return <div style={{ color: "#64748b" }}>No extras</div>;

                  return list.map((x) => (
                    <div key={x.rowId} style={{ display: "flex", gap: 12 }}>
                      <div
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                          Label
                        </div>
                        <div style={{ color: "#111827" }}>{x.label || "-"}</div>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          background: "#f8fafc",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                          Value
                        </div>
                        <div style={{ color: "#111827" }}>{String(x.value ?? "-")}</div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </ModalBoxWide>
        </Overlay>
      )}

      {/* ✅ UPDATED: Edit modal with all fields */}
      {editModal && selectedPackage && (
        <Overlay onClose={closeEditModal}>
          <ModalBoxWide onClick={(e) => e.stopPropagation()}>
            <ModalHeader title={`Edit Sponsorship #${selectedPackage.id}`} onClose={closeEditModal} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <Field label="Conference">
                <select
                  name="conference_id"
                  value={editForm.conference_id}
                  onChange={onEditChange}
                  style={selectStyle()}
                >
                  <option value="">Select</option>
                  {conferences.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name || c.title || c.conference_name || `Conference #${c.id}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Video Between Sessions">
                <select
                  name="video_show_between_sessions"
                  value={editForm.video_show_between_sessions}
                  onChange={onEditChange}
                  style={selectStyle()}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>

              <Field label="Item">
                <Input name="item" value={editForm.item} onChange={onEditChange} />
              </Field>

              <Field label="Price">
                <Input name="price" value={editForm.price} onChange={onEditChange} />
              </Field>

              <Field label="Max Sponsors">
                <Input
                  name="max_sponsors"
                  value={editForm.max_sponsors}
                  onChange={onEditChange}
                  type="number"
                />
              </Field>

              <Field label="Booth Size">
                <Input name="booth_size" value={editForm.booth_size} onChange={onEditChange} />
              </Field>

              <Field label="Booklet Ad">
                <Input name="booklet_ad" value={editForm.booklet_ad} onChange={onEditChange} />
              </Field>

              <Field label="Website Ad">
                <Input name="website_ad" value={editForm.website_ad} onChange={onEditChange} />
              </Field>

              <Field label="Bags Inserts">
                <Input name="bags_inserts" value={editForm.bags_inserts} onChange={onEditChange} />
              </Field>

              <Field label="Backdrop Logo">
                <Input name="backdrop_logo" value={editForm.backdrop_logo} onChange={onEditChange} />
              </Field>

              <Field label="Non-Residential Reg">
                <Input
                  name="non_residential_reg"
                  value={editForm.non_residential_reg}
                  onChange={onEditChange}
                  type="number"
                />
              </Field>

              <Field label="Residential Reg">
                <Input
                  name="residential_reg"
                  value={editForm.residential_reg}
                  onChange={onEditChange}
                  type="number"
                />
              </Field>
            </div>

            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button onClick={addEditExtra} style={btnGray}>
                Add Extra
              </button>

              <button
                onClick={() => handleDeleteClick(selectedPackage.id)}
                style={btnRed}
                disabled={deletingId === selectedPackage.id}
              >
                {deletingId === selectedPackage.id ? "Deleting..." : "Delete Package"}
              </button>
            </div>

            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              {editExtras.filter((x) => !x._delete).length === 0 && (
                <div style={{ color: "#64748b" }}>No extras. Click "Add Extra".</div>
              )}

              {editExtras
                .filter((x) => !x._delete)
                .map((row) => (
                  <div
                    key={row.rowId}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <Field label="Extra Label">
                        <Input
                          value={row.label}
                          onChange={(e) => updateEditExtra(row.rowId, "label", e.target.value)}
                          placeholder="Enter label (e.g., Booklet Ad)"
                        />
                      </Field>
                    </div>

                    <div style={{ flex: 1, minWidth: 240 }}>
                      <Field label="Extra Value">
                        <Input
                          value={row.value}
                          onChange={(e) => updateEditExtra(row.rowId, "value", e.target.value)}
                          placeholder="Enter value"
                        />
                      </Field>
                    </div>

                    <button onClick={() => deleteEditExtra(row.rowId)} style={btnDangerOutline}>
                      Delete
                    </button>
                  </div>
                ))}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button onClick={closeEditModal} disabled={saving} style={btnGray}>
                Cancel
              </button>
              <button onClick={handleUpdate} disabled={saving} style={btnGreen}>
                {saving ? "Finishing..." : "Update"}
              </button>
            </div>
          </ModalBoxWide>
        </Overlay>
      )}

      {/* All helper components and styles remain exactly the same */}
      {/* ... (copy all helper functions and styles from original code) ... */}
    </>
  );
}

// Copy ALL helper functions and styles from your original code (they remain unchanged)
// ... (Th, Td, DetailItem, Overlay, ModalHeader, ModalBox, ModalBoxWide, Field, Input, all styles)

function sanitizeUrl(url) {
  return String(url || "").trim().replace(/\\+$/g, "").replace(/\/+$/g, "");
}

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return "-";
  }
}

function Th({ children }) {
  return <th style={thStyle}>{children}</th>;
}

function Td({ children, ...rest }) {
  return (
    <td style={tdStyle} {...rest}>
      {children}
    </td>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={{ fontWeight: 400 }}>
      <div style={detailLabel}>{label}</div>
      <div style={detailValue}>{String(value)}</div>
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div style={overlayStyle} onClick={onClose}>
      {children}
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div style={modalHeaderStyle}>
      <h3 style={{ margin: 0, fontSize: 22, fontWeight: 400, color: "#1f2937" }}>{title}</h3>
      <button onClick={onClose} style={closeBtnStyle}>
        ✕
      </button>
    </div>
  );
}

function ModalBox({ children, ...rest }) {
  return (
    <div style={modalBoxStyle} {...rest}>
      {children}
    </div>
  );
}

function ModalBoxWide({ children, ...rest }) {
  return (
    <div style={modalBoxWideStyle} {...rest}>
      {children}
    </div>
  );
}

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };

function Field({ label, children }) {
  return (
    <div style={{ fontWeight: 400 }}>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        outline: "none",
        fontSize: 14,
        fontWeight: 400,
      }}
    />
  );
}

// All styles remain exactly the same
const errBox = {
  marginTop: 12,
  marginBottom: 12,
  padding: 12,
  borderRadius: 8,
  background: "#fef2f2",
  color: "#b91c1c",
  whiteSpace: "pre-wrap",
  fontWeight: 400,
};

const tableWrap = {
  marginTop: 12,
  overflowX: "auto",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  background: "#fff",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 1200, // Increased for more columns
  fontWeight: 400,
};

const thStyle = {
  textAlign: "left",
  padding: 12,
  background: "#f8fafc",
  borderBottom: "2px solid #e5e7eb",
  fontSize: 12,
  color: "#64748b",
  fontWeight: 400,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: 12,
  borderBottom: "1px solid #f1f5f9",
  fontSize: 14,
  whiteSpace: "nowrap",
  fontWeight: 400,
};

const detailLabel = {
  fontSize: 12,
  color: "#6b7280",
  fontWeight: 400,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const detailValue = {
  fontSize: 15,
  fontWeight: 400,
  color: "#111827",
  padding: "10px 12px",
  background: "#f8fafc",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 20,
  fontWeight: 400,
};

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: "1px solid #e5e7eb",
  fontWeight: 400,
};

const closeBtnStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const modalBoxStyle = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  width: "100%",
  maxWidth: 420,
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  fontWeight: 400,
};

const modalBoxWideStyle = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  width: "100%",
  maxWidth: 900, // Increased for more fields
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  fontWeight: 400,
};

const fieldLabelStyle = {
  fontSize: 12,
  color: "#6b7280",
  fontWeight: 400,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const selectStyle = (extra = {}) => ({
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  outline: "none",
  fontSize: 14,
  background: "white",
  fontWeight: 400,
  ...extra,
});

const btnGray = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnPrimary = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "#9B1321",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 400,
};

const btnRed = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
  color: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnGreen = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #16a34a 0%, #16a34a 100%)",
  color: "white",
  cursor: "pointer",
  fontWeight: 400,
};

const btnDangerOutline = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #ef4444",
  background: "white",
  color: "#b91c1c",
  cursor: "pointer",
  fontWeight: 400,
};

const btnView = (disabled) => ({
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  border: "1px solid #3b82f6",
  background: disabled ? "#e5e7eb" : "#dbeafe",
  color: "#1d4ed8",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});

const btnEdit = (disabled) => ({
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  border: "1px solid #a855f7",
  background: disabled ? "#e5e7eb" : "#f3e8ff",
  color: "#6b21a8",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});

const btnDelete = (disabled) => ({
  padding: "6px 12px",
  borderRadius: 6,
  border: "none",
  background: disabled ? "#9ca3af" : "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
  color: "white",
  fontSize: 12,
  fontWeight: 400,
  cursor: disabled ? "not-allowed" : "pointer",
});
